import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setStatusMessage('Спасибо за подписку!');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    setStatus('loading');

    try {
      const res = await fetch('https://readdy.ai/api/form/d956sik41f1vs39a6u3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('Спасибо за подписку!');
        setEmail('');
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || 'Произошла ошибка. Попробуйте позже.';
        if (serverMsg.toLowerCase().includes('spam')) {
          setStatus('success');
          setStatusMessage('Спасибо за подписку!');
          setTimeout(() => setStatus('idle'), 3000);
        } else {
          setStatus('error');
          setStatusMessage(serverMsg);
          setTimeout(() => setStatus('idle'), 5000);
        }
      }
    } catch {
      setStatus('error');
      setStatusMessage('Ошибка сети. Проверьте подключение.');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <footer id="contact" className="w-full px-6 md:px-10 py-16 md:py-20 bg-foreground-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <h3 className="text-2xl md:text-3xl font-heading font-semibold text-background-50 mb-3">
              {t('footer.subscribe_title')}
            </h3>
            <p className="text-sm text-background-50/50 mb-6 leading-relaxed max-w-sm">
              {t('footer.subscribe_desc')}
            </p>
            <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col sm:flex-row gap-3 max-w-md">
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.subscribe_placeholder')}
                className="flex-1 px-4 py-3 bg-transparent border-b-2 border-background-50/20 text-background-50 text-sm placeholder:text-background-50/30 focus:outline-none focus:border-accent-500 transition-colors"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="website-alt-field"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-6 py-3 rounded-full bg-background-50 text-foreground-900 text-sm font-semibold hover:bg-accent-200 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 inline-flex items-center gap-2"
              >
                <span>{status === 'loading' ? '...' : t('footer.subscribe_btn')}</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            </form>
            {status === 'success' && (
              <p className="mt-3 text-sm text-green-400">{statusMessage}</p>
            )}
            {status === 'error' && (
              <p className="mt-3 text-sm text-red-400">{statusMessage}</p>
            )}
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm font-semibold text-background-50/60 uppercase tracking-wider mb-4">
              {t('footer.categories_title')}
            </h4>
            <ul className="space-y-2.5">
              {['Shoes', 'Hoodies', 'T-Shirts', 'Jackets', 'Bags', 'Accessories'].map((cat) => {
                const catUrls: Record<string, string> = {
                  'Shoes': 'https://kaspreadsheet.com/category-911-Shoes.html',
                  'Hoodies': 'https://kaspreadsheet.com/search-912/name/Shoes.html',
                  'T-Shirts': 'https://kaspreadsheet.com/search-913/name/Shoes.html',
                  'Jackets': 'https://kaspreadsheet.com/search-914/name/Shoes.html',
                  'Bags': 'https://kaspreadsheet.com/search-921/name/Shoes.html',
                  'Accessories': 'https://kaspreadsheet.com/search-920/name/Shoes.html',
                };
                return (
                <li key={cat}>
                  <a
                    href={catUrls[cat]}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/50 hover:text-background-50 transition-colors underline decoration-background-50/20 hover:decoration-background-50/60 whitespace-nowrap"
                  >
                    {cat}
                  </a>
                </li>
                );
              })}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-sm font-semibold text-background-50/60 uppercase tracking-wider mb-4">
              {t('footer.contact_title')}
            </h4>
            <p className="text-sm text-background-50/50 leading-relaxed">
              {t('footer.address')}
            </p>
            <div className="mt-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-background-50/50 hover:text-background-50 transition-colors underline decoration-background-50/20 hover:decoration-background-50/60"
              >
                kaspreadsheet.com
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-50/30">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-6">
            <a href="/" className="text-xs text-background-50/30 hover:text-background-50/60 transition-colors underline decoration-background-50/10">
              {t('footer.privacy')}
            </a>
            <a href="/" className="text-xs text-background-50/30 hover:text-background-50/60 transition-colors underline decoration-background-50/10">
              {t('footer.terms')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}